<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '~/stores/session'

const route = useRoute()
const session = useSessionStore()

// Whether the current navigation needs auth verification before rendering.
const needsAuth = computed(() => !!route.meta.requiresAuth)

// Is the gate currently blocking (showing the loading surface)?
// For public routes: never.
// For auth routes: block while authenticated is still null (unknown).
const isBlocking = computed(() => {
  if (!needsAuth.value) return false
  // If we already know the auth state (even if false), we can render.
  // The guard (beforeEach) has already redirected by this point if auth=false.
  return session.authenticated === null
})

// On every navigation to a requiresAuth route, trigger ensure() if we haven't resolved yet.
watch(
  () => route.path,
  () => {
    if (needsAuth.value && session.authenticated === null) {
      session.ensure().catch(() => {
        // Swallow — the guard has already redirected or will redirect on the next tick.
      })
    }
  },
  { immediate: true }
)
</script>

<template>
  <div style="min-height: 100vh;">
    <!-- Neutral loading surface for auth routes while auth state is unknown -->
    <div
      v-if="isBlocking"
      style="display: flex; align-items: center; justify-content: center; min-height: 100vh;"
    >
      <!-- Minimal spinner using the shared .spin class from main.css -->
      <div
        class="spin"
        style="width: 24px; height: 24px; border: 2px solid var(--dq-border); border-top-color: var(--dq-accent); border-radius: 50%;"
      />
    </div>

    <!-- Render the route once auth is resolved (or immediately for public routes) -->
    <slot v-else />
  </div>
</template>

<style scoped>
@keyframes spin {
  to { transform: rotate(360deg); }
}
.spin {
  animation: spin 0.8s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .spin {
    animation: none;
    border-top-color: var(--dq-border);
  }
}
</style>
